/**
 * <sell-through-ring> — one quota drawn as an arc, with its own two figures at
 * the centre: what has sold over what there is. It is the 56 px mark the
 * programme calendar draws beside every ticket type.
 *
 * Altitude decides the shape, not affinity: the ring is the PER-TYPE mark, on
 * the row that owns that type, and the whole-event roll-up is a bar — the
 * console's identity band and the event tile both draw <event-sale-progress>
 * for it. Two readings, two shapes, so neither is mistaken for the other.
 *
 * A ring rather than a linear meter because a full-width bar was the calendar
 * row's only strong horizontal mark, so the eye read the measurement as the
 * boundary between types; a ring claims no horizontal span.
 *
 * THREE STATES THE ARC MUST KEEP APART, and it is the linecap that decides two
 * of them. `stroke-linecap="round"` paints a cap dot for any stroke, including
 * a zero-length one — so a quota with nothing sold and a quota with three sold
 * against a hundred thousand drew the identical 3 px dot, and the mark said
 * "the first tickets are in" where none were. The arc is therefore drawn only
 * when something has actually sold: nothing sold is a clean track, the first
 * sale is a single cap, and everything above that is a real sweep.
 *
 * A quota-less type (capacity 0) draws NO arc at all: 0 of 0 has no
 * proportion, and a full arc would say sold out where the type simply has no
 * quota yet. A type that sold past its quota draws a FULL arc — the sweep is
 * clamped, never the digits, so the centre still states the true count above
 * the quota it passed.
 *
 * `sold` null is not zero: a mount that cannot read the figure at all (no
 * sales-report grant, an editor opened away from the console that holds this
 * event's orders) prints the quota alone and draws nothing, because a row
 * stating 0 where it cannot see states a fabrication.
 *
 * A STOPPED QUOTA IS A RECORD, NOT A METER. `stopped` says the figure can no
 * longer move — a voucher past its expiry, a code whose allowance is spent —
 * and the arc goes to the outline tone: the mark is still drawn, it simply no
 * longer carries the surface's live ink, and the sold-out green is withheld,
 * since a full arc in success tone would congratulate an expiry. Without it a
 * code stopped at 288 of 300 drew a live 96 % arc, which is a false statement
 * rather than a missing feature. No other colour is spent on the state: a
 * spent press code did its whole job and a spent newsletter code is turning
 * buyers away, and nothing on either document says which.
 *
 * `accessibleName` is what the mark is called where its two figures are not
 * tickets over a capacity. The sentence this component composes for itself
 * says SOLD, so a consumer counting something else — a code's redemptions
 * against its allowance — hands the ring its own sentence rather than letting
 * the arc announce a sale that never happened.
 *
 * Stroke colours are the theme tokens BY NAME rather than utility classes: an
 * SVG presentation attribute is not a surface Tailwind's JIT scans, and a
 * class that silently failed to compile would paint the ring black.
 */

import { formatNumber } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";

// r=15.5 inside a 36 box leaves the 3-wide stroke clear of the edge.
const CIRCUMFERENCE = 2 * Math.PI * 15.5;

const STROKE_WIDTH = 3;

const TRACK_STROKE = "var(--schmancy-sys-color-surface-containerHighest)";
const SOLD_OUT_STROKE = "var(--schmancy-sys-color-success-default)";
const SELLING_STROKE = "var(--schmancy-sys-color-primary-default)";
const STOPPED_STROKE = "var(--schmancy-sys-color-outline)";

@schmancyElement("sell-through-ring")
export class SellThroughRing extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: contents;
			}
		`,
	];

	@property({ type: Number }) sold: number | null = null;

	@property({ type: Number }) capacity = 0;

	/** One scale, and `render()` never reads this. It survives because mounts
	 *  spell `size="row"` in their own markup, and a removed property leaves a
	 *  written attribute unknown to lit-analyzer; it retires with the last
	 *  mount that spells it. */
	@property({ type: String }) size: "row" = "row";

	/** The figure can no longer move — see the module note. Default false, so
	 *  every mount that measures a live quota is untouched. */
	@property({ type: Boolean }) stopped = false;

	/** The sentence assistive tech reads for this mark, where the two figures
	 *  are not tickets sold against a capacity. Null keeps the component's own.
	 */
	@property({ attribute: false }) accessibleName: string | null = null;

	override render(): TemplateResult {
		const soldOut =
			!this.stopped &&
			this.sold !== null &&
			this.capacity > 0 &&
			this.capacity - this.sold <= 0;
		const sweep =
			this.sold === null || this.capacity <= 0
				? 0
				: Math.min(1, this.sold / this.capacity);
		const capacityText = formatNumber(this.capacity, this.locale);
		return html`
			<span
				class="relative grid size-14 shrink-0 place-items-center"
				role="img"
				aria-label=${
					this.accessibleName ??
					(this.sold === null
						? this.t("admin.events.console.capacityAria", {
								capacity: capacityText,
							})
						: this.t("admin.events.console.sellThroughAria", {
								sold: formatNumber(this.sold, this.locale),
								capacity: capacityText,
							}))
				}
			>
				<svg viewBox="0 0 36 36" class="size-14 -rotate-90" aria-hidden="true">
					<circle
						cx="18"
						cy="18"
						r="15.5"
						fill="none"
						stroke=${TRACK_STROKE}
						stroke-width=${STROKE_WIDTH}
					></circle>
					<circle
						cx="18"
						cy="18"
						r="15.5"
						fill="none"
						stroke=${
							sweep === 0
								? "none"
								: this.stopped
									? STOPPED_STROKE
									: soldOut
										? SOLD_OUT_STROKE
										: SELLING_STROKE
						}
						stroke-width=${STROKE_WIDTH}
						stroke-linecap="round"
						stroke-dasharray=${CIRCUMFERENCE}
						stroke-dashoffset=${CIRCUMFERENCE * (1 - sweep)}
					></circle>
				</svg>
				<span class="absolute flex flex-col items-center">
					${when(
						this.sold !== null,
						() => html`
							<schmancy-typography
								type="label"
								token="sm"
								class=${this.classMap({
									"tabular-nums leading-none": true,
									"text-success-default": soldOut,
								})}
							>
								${formatNumber(this.sold ?? 0, this.locale)}
							</schmancy-typography>
							<span class="my-0.5 h-px w-6 bg-outline"></span>
						`,
					)}
					<schmancy-typography
						type="label"
						token=${this.sold === null ? "sm" : "xs"}
						class=${this.classMap({
							"tabular-nums leading-none": true,
							"text-surface-onVariant": this.sold !== null,
						})}
					>
						${capacityText}
					</schmancy-typography>
				</span>
			</span>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"sell-through-ring": SellThroughRing;
	}
}
